import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "./Constants.js";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import "./Upload.css";

const Upload = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);

  const [formData, setFormData] = useState({
    emp_code: "",
    department: "",
    question_id: "",
    question_type: "",
    question: "",
    answer: "",
    rating: "",
  });

  // ================= INITIAL LOAD =================
  useEffect(() => {
    fetchProfile();
    fetchDepartments();
  }, []);

  // ================= FETCH PROFILE =================
  const fetchProfile = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      setUser(data);

      setFormData((prev) => ({
        ...prev,
        emp_code: data?.profile?.emp_code || "",
      }));
    } catch (err) {
      setError("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH DEPARTMENTS =================
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${BASE_URL}dept`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      setDepartments(data.departments || []);
    } catch (err) {
      setError("Failed to fetch departments");
    }
  };

  // ================= FETCH QUESTION TYPES BY DEPT =================
  const fetchQuestionTypesByDept = async (dept) => {
    try {
      const token = localStorage.getItem("authToken");

      const response = await fetch(
        "https://pms.vdcapp.in/backend/getQuestionByDept",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ dept }),
        }
      );

      const data = await response.json();

      if (data.status) {
        setQuestionTypes(data.data);
      } else {
        setQuestionTypes([]);
      }
    } catch (err) {
      console.error("Error fetching question types:", err);
      setQuestionTypes([]);
    }
  };

  // ================= HANDLE INPUT =================
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Department change
    if (name === "department") {
      setFormData((prev) => ({
        ...prev,
        department: value,
        question_id: "",
        question_type: "",
      }));

      fetchQuestionTypesByDept(value);
    }

    // Question Type Dropdown
    else if (name === "question_id") {
      const selected = questionTypes.find(
        (q) => q.id.toString() === value
      );

      setFormData((prev) => ({
        ...prev,
        question_id: value,
        question_type: selected ? selected.question_type : "",
      }));
    }

    // Rating
    else if (name === "rating") {
      let ratingValue = Number(value);
      if (ratingValue > 5) ratingValue = 5;
      if (ratingValue < 1) ratingValue = 1;

      setFormData((prev) => ({
        ...prev,
        rating: ratingValue,
      }));
    }

    else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Submitting Payload:", formData);

    const token = localStorage.getItem("authToken");
    const form = new FormData();

    Object.entries(formData).forEach(([key, value]) =>
      form.append(key, value)
    );

    try {
      const response = await fetch(`${BASE_URL}insertquestion`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await response.json();

      if (!response.ok || data.status === false) {
        alert(data.message);
        return;
      }

      alert("Question submitted successfully");

      setFormData({
        emp_code: user?.profile?.emp_code || "",
        department: "",
        question_id: "",
        question_type: "",
        question: "",
        answer: "",
        rating: "",
      });

      setQuestionTypes([]);

    } catch (err) {
      alert("Something went wrong");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-md-12 grid-margin">
              <h4>Viva Question Details</h4>
              <div className="card">
                <div className="card-body">

                  <form onSubmit={handleSubmit}>
                    <div className="row">

                      {/* Employee Code */}
                      <div className="col-md-3">
                        <label>Employee Code</label>
                        <input
                          type="text"
                          name="emp_code"
                          className="form-control input-font"
                          value={formData.emp_code}
                          readOnly
                        />
                      </div>

                      {/* Department */}
                      <div className="col-md-3">
                        <label>Department</label>
                        <select
                          name="department"
                          className="form-control input-font"
                          style={{ padding: "0px 10px" }}
                          value={formData.department}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Department</option>
                          {departments.map((dept, index) => (
                            <option key={index} value={dept.dept_name}>
                              {dept.dept_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Question Type Dropdown (from API) */}
                      {questionTypes.length > 0 && (

                        <div className="col-md-3">
                          <label>Question Type</label>
                          <select
                            name="question_id"
                            className="form-control input-font"
                            style={{ padding: "0px 10px" }}
                            value={formData.question_id}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Question Type</option>
                            {questionTypes.map((q) => (
                              <option key={q.id} value={q.id}>
                                {q.question_type}
                              </option>
                            ))}
                          </select>
                        </div>

                      )}

                      {/* Rating */}
                      <div className="col-md-3">
                        <label>Rating</label>
                        <input
                          type="number"
                          name="rating"
                          min="1"
                          max="5"
                          className="form-control input-font"
                          value={formData.rating}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>



                    {/* Question Textarea */}
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <label>Question</label>
                        <textarea
                          name="question"
                          className="form-control"
                          rows="3"
                          value={formData.question}
                          onChange={handleInputChange}
                          placeholder="Enter question"
                          required
                        />
                      </div>
                    </div>

                    {/* Answer Textarea */}
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <label>Answer</label>
                        <textarea
                          name="answer"
                          className="form-control"
                          rows="3"
                          value={formData.answer}
                          onChange={handleInputChange}
                          placeholder="Enter answer"
                          required
                        />
                      </div>
                    </div>


                    <br />
                    <button className="btn btn-primary">Submit</button>

                  </form>

                </div>
              </div>
            </div>
          </div>

          <footer className="footer">
            © {new Date().getFullYear()} Vijaya Diagnostic Centre
          </footer>

        </div>
      </div>
    </div>
  );
};

export default Upload;
